<template>
	<section v-loading="loading" class="provider-store-change-page">
		<div class="page-heading">
			<div>
				<h2>{{ pageTitle }}</h2>
				<p>{{ pageDescription }}</p>
			</div>
			<el-button icon="el-icon-back" @click="handleBack">返回我的店铺</el-button>
		</div>

		<div class="change-notice">
			<i class="el-icon-info"></i>
			<span>{{ noticeText }}</span>
		</div>

		<el-form ref="changeForm" :model="formData" :rules="formRules" label-position="top" class="change-form">
			<div class="change-form-section">
				<div class="change-form-heading">
					<span>基础信息</span>
					<small>修改店铺及经营主体资料</small>
				</div>
				<el-row :gutter="28">
					<el-col :span="12">
						<el-form-item label="店铺名称" prop="shopName">
							<el-input v-model.trim="formData.shopName" maxlength="50" show-word-limit placeholder="请输入店铺名称"></el-input>
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="联系人" prop="contactPerson">
							<el-input v-model.trim="formData.contactPerson" maxlength="20" placeholder="请输入联系人"></el-input>
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="联系方式" prop="contactPhone">
							<el-input v-model.trim="formData.contactPhone" maxlength="20" placeholder="请输入手机号或座机号"></el-input>
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="经营主体名称" prop="companyName">
							<el-input v-model.trim="formData.companyName" maxlength="80" placeholder="请输入经营主体名称"></el-input>
						</el-form-item>
					</el-col>
					<el-col :span="24">
						<el-form-item label="经营地址" prop="businessAddress">
							<el-input v-model.trim="formData.businessAddress" maxlength="120" placeholder="请输入经营地址"></el-input>
						</el-form-item>
					</el-col>
					<el-col :span="24">
						<el-form-item label="业务简介" prop="businessDesc">
							<el-input
								v-model.trim="formData.businessDesc"
								type="textarea"
								:rows="5"
								maxlength="800"
								show-word-limit
								resize="none"
								placeholder="请输入店铺业务简介，不超过800字"
							></el-input>
						</el-form-item>
					</el-col>
				</el-row>
			</div>

			<div class="change-form-section">
				<div class="change-form-heading">
					<span>结算信息</span>
					<small>修改店铺结算账户信息</small>
				</div>
				<el-row :gutter="28">
					<el-col :span="12">
						<el-form-item label="开户行名称" prop="bankName">
							<el-input v-model.trim="formData.bankName" maxlength="80" placeholder="请输入开户行名称"></el-input>
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="开户行账号" prop="bankAccount">
							<el-input v-model.trim="formData.bankAccount" maxlength="40" placeholder="请输入开户行账号"></el-input>
						</el-form-item>
					</el-col>
				</el-row>
			</div>

			<div class="change-form-section">
				<div class="change-form-heading">
					<span>附件资料</span>
					<small>{{ attachmentDescription }}</small>
				</div>
				<el-row :gutter="28">
					<el-col :span="12">
						<el-form-item label="店铺 Logo">
							<div v-if="currentLogoUrl" class="current-file">
								<span>当前 Logo</span>
								<el-image :src="currentLogoUrl" :preview-src-list="[currentLogoUrl]" fit="cover"></el-image>
							</div>
							<upload-images
								v-model="logoFiles"
								:action="uploadAction"
								:data="logoUploadData"
								:headers="uploadHeaders"
								:preview-url="previewUrl"
								:delete-api="deleteApi"
								:delete-params="logoUploadData"
								:limit="1"
								:size="5"
								:tip="true"
							></upload-images>
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="营业执照" prop="businessLicenseFiles">
							<div v-if="currentBusinessLicenseUrl" class="current-file">
								<span>当前营业执照</span>
								<el-image :src="currentBusinessLicenseUrl" :preview-src-list="[currentBusinessLicenseUrl]" fit="cover"></el-image>
							</div>
							<upload-images
								v-model="businessLicenseFiles"
								:action="uploadAction"
								:data="businessLicenseUploadData"
								:headers="uploadHeaders"
								:preview-url="previewUrl"
								:delete-api="deleteApi"
								:delete-params="businessLicenseUploadData"
								:limit="1"
								:size="5"
								:tip="true"
							></upload-images>
						</el-form-item>
					</el-col>
				</el-row>
			</div>

			<div class="form-actions">
				<el-button @click="handleBack">取消</el-button>
				<el-button type="primary" :loading="submitLoading" @click="handleSubmit">{{ submitButtonText }}</el-button>
			</div>
		</el-form>
	</section>
</template>

<script>
	import { mapGetters } from 'vuex'
	import UploadImages from '@/components/UploadImages'
	import {
		changeSubmitMerchantShopApply,
		deleteMerchantFile,
		previewMerchantFile,
		queryMerchantFileList,
		uploadMerchantFile
	} from '@/api/mall'
	import { SHOP_AUDIT_STATE, SHOP_APPLY_TYPE, SHOP_STATE } from '@/enum'
	import { getUuid } from '@/utils'
	import { getToken } from '@/utils/auth'

	const createFormData = () => ({
		id: getUuid(),
		shopName: '',
		contactPerson: '',
		contactPhone: '',
		companyName: '',
		businessAddress: '',
		businessDesc: '',
		bankName: '',
		bankAccount: '',
		businessLicenseFiles: []
	})

	export default {
		name: 'ProviderStoreChange',
		components: { UploadImages },
		data() {
			return {
				loading: false,
				submitLoading: false,
				formData: createFormData(),
				logoFiles: [],
				businessLicenseFiles: [],
				currentFileList: [],
				uploadAction: uploadMerchantFile(),
				previewUrl: previewMerchantFile(),
				deleteApi: deleteMerchantFile,
				formRules: {
					shopName: [{ required: true, message: '请输入店铺名称', trigger: 'blur' }],
					contactPerson: [{ required: true, message: '请输入联系人', trigger: 'blur' }],
					contactPhone: [
						{ required: true, message: '请输入联系方式', trigger: 'blur' },
						{ pattern: /^1[3-9]\d{9}$|^0\d{2,3}-?\d{7,8}$/, message: '请输入正确的手机号或座机号', trigger: 'blur' }
					],
					companyName: [{ required: true, message: '请输入经营主体名称', trigger: 'blur' }],
					businessAddress: [{ required: true, message: '请输入经营地址', trigger: 'blur' }],
					businessDesc: [
						{ required: true, message: '请输入业务简介', trigger: 'blur' },
						{ max: 800, message: '业务简介不能超过800字', trigger: 'blur' }
					],
					bankName: [{ required: true, message: '请输入开户行名称', trigger: 'blur' }],
					bankAccount: [{ required: true, message: '请输入开户行账号', trigger: 'blur' }],
					businessLicenseFiles: [{ validator: this.validateBusinessLicense, trigger: 'change' }]
				}
			}
		},
		computed: {
			...mapGetters(['merchantShopInfo', 'merchantLatestApply']),
			// 同一表单组件同时承载资料变更和闭店重启，通过路由名称切换业务模式。
			isRestartMode() {
				return this.$route.name === 'ProviderStoreRestart'
			},
			pageTitle() {
				return this.isRestartMode ? '重启店铺' : '店铺信息变更'
			},
			pageDescription() {
				return this.isRestartMode
					? '确认并完善店铺资料后提交审核，审核通过后店铺将恢复经营'
					: '修改店铺资料并提交平台审核，审核通过后新资料才会生效'
			},
			noticeText() {
				return this.isRestartMode
					? '表单已根据闭店前的店铺信息自动填充，可直接确认或修改；未重新上传附件时将沿用当前资料。'
					: '表单已根据当前店铺信息自动填充。附件作为本次变更申请资料单独保存，营业执照需重新上传。'
			},
			attachmentDescription() {
				return this.isRestartMode ? '可沿用现有附件，也可上传新资料' : '新附件将在资料变更审核通过后生效'
			},
			submitButtonText() {
				return this.isRestartMode ? '提交重启申请' : '提交变更申请'
			},
			// 当前用户店铺信息由 myShop 接口加载，用于回填资料变更表单。
			shopInfo() {
				return this.merchantShopInfo || {}
			},
			// 店铺附件上传接口通过登录令牌识别当前用户。
			uploadHeaders() {
				return { InternetToken: getToken() }
			},
			// 新上传附件绑定本次变更申请 ID，不直接修改当前店铺附件。
			logoUploadData() {
				return { businessId: this.formData.id, fileBusinessType: 'SHOP_LOGO' }
			},
			// 营业执照与 Logo 使用不同业务类型，便于审核详情按类型展示。
			businessLicenseUploadData() {
				return { businessId: this.formData.id, fileBusinessType: 'BUSINESS_LICENSE' }
			},
			// 当前附件仅用于变更前对照，不写入本次申请的上传列表。
			currentLogoUrl() {
				return this.getCurrentFileUrl('SHOP_LOGO')
			},
			currentBusinessLicenseUrl() {
				return this.getCurrentFileUrl('BUSINESS_LICENSE')
			}
		},
		watch: {
			// 同步新营业执照到提交数据，并在上传或删除后重新触发表单校验。
			businessLicenseFiles() {
				this.formData.businessLicenseFiles = this.businessLicenseFiles
				this.$nextTick(() => {
					this.$refs.changeForm && this.$refs.changeForm.validateField('businessLicenseFiles')
				})
			}
		},
		created() {
			this.initPage()
		},
		methods: {
			// 资料变更必须重新上传营业执照；重启店铺允许沿用闭店前的营业执照。
			validateBusinessLicense(rule, value, callback) {
				const canUseCurrentFile = this.isRestartMode && Boolean(this.currentBusinessLicenseUrl)
				if (!this.businessLicenseFiles.length && !canUseCurrentFile) {
					callback(new Error(this.isRestartMode ? '请上传营业执照' : '请重新上传营业执照'))
				} else callback()
			},
			// 刷新当前店铺和最新申请，并按当前模式校验正常经营或已闭店状态。
			async initPage() {
				this.loading = true
				try {
					await this.$store.dispatch('merchantShop/loadShopContext', true)
					const requiredState = this.isRestartMode ? SHOP_STATE.CLOSED : SHOP_STATE.NORMAL
					if (!this.shopInfo.id || Number(this.shopInfo.shopState) !== requiredState) {
						this.$message.warning(this.isRestartMode ? '只有已闭店的店铺才能申请重启' : '只有正常经营的店铺才能提交资料变更')
						this.$router.replace('/provider/store')
						return
					}
					if (this.merchantLatestApply && Number(this.merchantLatestApply.auditState) === SHOP_AUDIT_STATE.PENDING) {
						this.$message.warning('已有待审核申请，请等待平台审核')
						this.$router.replace('/provider/store')
						return
					}
					this.fillForm(this.shopInfo)
					await this.loadCurrentFiles(this.shopInfo.id)
				} finally {
					this.loading = false
				}
			},
			// 兼容店铺接口中的历史字段名称，并保留本次申请的新 ID。
			fillForm(shop) {
				this.formData = {
					...this.formData,
					shopName: shop.shopName || shop.storeName || '',
					contactPerson: shop.contactPerson || shop.contactName || shop.linkMan || '',
					contactPhone: shop.contactPhone || shop.telephone || shop.mobile || '',
					companyName: shop.companyName || shop.subjectName || '',
					businessAddress: shop.businessAddress || shop.address || '',
					businessDesc: shop.businessDesc || shop.businessScope || shop.introduction || '',
					bankName: shop.bankName || '',
					bankAccount: shop.bankAccount || ''
				}
			},
			// 查询当前店铺附件，仅供用户对照现有资料。
			async loadCurrentFiles(businessId) {
				const response = await queryMerchantFileList({ businessId }).catch(() => ({ data: [] }))
				this.currentFileList = Array.isArray(response.data) ? response.data : []
			},
			// 兼容不同附件 ID 字段并生成文件预览地址。
			getCurrentFileUrl(fileBusinessType) {
				const file = this.currentFileList.find(item => item.fileBusinessType === fileBusinessType)
				const fileId = file && (file.id || file.fileId || file.fileRecordId)
				return fileId ? previewMerchantFile() + fileId : (file && file.url) || ''
			},
			// 变更流程中的返回操作统一回到新服务商端“我的店铺”。
			handleBack() {
				this.$router.push('/provider/store')
			},
			// 表单校验完成后增加二次确认，避免误提交店铺资料审核。
			handleSubmit() {
				this.$refs.changeForm.validate(valid => {
					if (!valid) return
					this.$modal
						.confirm(
							this.isRestartMode ? '重启店铺需平台审核通过后生效，确定提交吗？' : '资料变更需平台审核通过后生效，确定提交吗？'
						)
						.then(() => this.submitStoreApply())
						.catch(() => {})
				})
			},
			// 重启店铺与资料变更均沿用旧版 changeSubmit 接口，以 applyType 区分具体业务。
			async submitStoreApply() {
				this.submitLoading = true
				try {
					const submitData = {
						...this.formData,
						applyType: this.isRestartMode ? SHOP_APPLY_TYPE.REAPPLY : SHOP_APPLY_TYPE.CHANGE,
						contactName: this.formData.contactPerson,
						telephone: this.formData.contactPhone,
						subjectName: this.formData.companyName
					}
					await changeSubmitMerchantShopApply(submitData)
					this.$message.success(this.isRestartMode ? '重启店铺申请已提交，请等待平台审核' : '资料变更申请已提交，请等待平台审核')
					await this.$store.dispatch('merchantShop/loadShopContext', true)
					// 最新申请接口尚未同步时先写入本次申请，保证店铺页立即显示待审核状态。
					if (
						this.merchantLatestApply.id !== submitData.id ||
						Number(this.merchantLatestApply.auditState) !== SHOP_AUDIT_STATE.PENDING
					) {
						this.$store.commit('merchantShop/SET_LATEST_APPLY', {
							...submitData,
							auditState: SHOP_AUDIT_STATE.PENDING
						})
					}
					this.$router.replace('/provider/store')
				} finally {
					this.submitLoading = false
				}
			}
		}
	}
</script>

<style lang="scss" scoped>
	.provider-store-change-page {
		min-height: 100%;
		padding: 24px;
		border: 1px solid #e4e9ef;
		border-radius: 8px;
		background: #ffffff;
		box-sizing: border-box;
	}

	.page-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding-bottom: 19px;
		border-bottom: 1px solid #e9edf2;

		h2 {
			margin: 0 0 6px;
			color: #303846;
			font-size: 21px;
		}
		p {
			margin: 0;
			color: #929aa6;
			font-size: 13px;
		}
	}

	.change-notice {
		margin-top: 16px;
		padding: 12px 15px;
		border: 1px solid #cfe1f5;
		border-radius: 6px;
		color: #52749a;
		font-size: 13px;
		background: #f2f8ff;

		i {
			margin-right: 7px;
			color: #2671cb;
		}
	}

	.change-form-section {
		margin-top: 16px;
		padding: 18px 22px 6px;
		border: 1px solid #e4e9ef;
		border-radius: 8px;
		background: #fbfcfd;
	}

	.change-form-heading {
		display: flex;
		align-items: center;
		margin-bottom: 16px;
		padding-bottom: 12px;
		border-bottom: 1px solid #e7edf4;
		color: #2f3b4d;

		&::before {
			width: 4px;
			height: 17px;
			margin-right: 9px;
			border-radius: 2px;
			background: linear-gradient(180deg, #3788df, #216bc1);
			content: '';
		}
		span {
			font-size: 16px;
			font-weight: 600;
		}
		small {
			margin-left: 12px;
			color: #9aa3af;
			font-size: 12px;
			font-weight: normal;
		}
	}

	.change-form {
		::v-deep .el-form-item {
			margin-bottom: 14px;
		}
		::v-deep .el-form-item__label {
			padding-bottom: 6px;
			color: #566273;
			font-weight: 500;
			line-height: 20px;
		}
	}

	.current-file {
		display: flex;
		align-items: center;
		margin-bottom: 12px;
		padding: 10px 12px;
		border: 1px solid #e5eaf0;
		border-radius: 6px;
		background: #f7f9fb;

		> span {
			margin-right: 12px;
			color: #7d8897;
			font-size: 12px;
		}
		.el-image {
			width: 76px;
			height: 56px;
			border-radius: 4px;
		}
	}

	.form-actions {
		padding: 26px 0 5px;
		text-align: center;

		.el-button {
			min-width: 130px;
		}
	}
</style>
